import {
  FacilityRegistrationFormInput,
  generatePassword,
  getFacilityRegistrationFormDataDefaultValues,
  getFacilityRegistrationFormDataSchema,
  parse,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  FormField,
  FormFieldContainer,
  Input,
  MultilineInput,
  OrganizationSelector,
  PageName,
  PasswordInput,
  buttonStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  createFacility,
  useGetErrorMessage,
  useListedOrganizations,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/facilities/registration")({
  component: Component,
});

function Component() {
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const defaultValues = useMemo(function (): FacilityRegistrationFormInput {
    return {
      ...getFacilityRegistrationFormDataDefaultValues(),
      password: generatePassword(),
    };
  }, []);

  const { Field, Subscribe, handleSubmit, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(getFacilityRegistrationFormDataSchema(), input);
      console.debug("フォームが送信されました。", output);
      try {
        const { facility } = await createFacility({
          ...output,
          organizationKey: output.organization.organizationKey,
        });

        await $alert({
          color: "success",
          content: t("%Register.2%", { $: { name: facility.name } }),
          title: t("%Register.0%"),
        });

        await navigate({ to: "/management/facilities", ignoreBlocker: true });
      } catch (error) {
        showError(error);
      }
    },
    validators: {
      onChange: getFacilityRegistrationFormDataSchema(),
    },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: t("%Form.1%"),
              no: t("%Stay.0%"),
              title: t("%Form.0%"),
              yes: t("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const { fetchNextPage, loaded, loading, organizations } = useListedOrganizations({ limit: 25 });

  return (
    <main>
      <PageName>{t("%FacilityInformation.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>{t("%FacilityInformation.0%")}</p>
      <form
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginTop: "16px",
        })}
        onSubmit={function (event) {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "320px" })}
                id={field.name}
                label={t("%FacilityName.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
          name="name"
        />
        <Field
          children={function (field) {
            return (
              <FormFieldContainer
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                invalid={field.state.meta.errors.length > 0}
                label={t("%Organization.0%")}
              >
                <OrganizationSelector
                  className={css({ maxWidth: "320px", width: "100%" })}
                  load={async function () {
                    await fetchNextPage();
                  }}
                  loaded={loaded}
                  loading={loading}
                  onBlur={field.handleBlur}
                  onChange={function (organization) {
                    field.handleChange(organization);
                  }}
                  options={organizations}
                  title={t("%Organization.0%")}
                  value={field.state.value}
                />
              </FormFieldContainer>
            );
          }}
          name="organization"
        />
        <Field name="representativeName">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%Representative.1%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%PhoneNumber.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
          name="phoneNumber"
        />
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%PhoneNumberLogin.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.checked)}
                title=""
                type="checkbox"
                value={field.state.value.toString()}
              />
            );
          }}
          name="phoneNumberLogin"
        />
        <Field name="emailAddress">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "320px" })}
                id={field.name}
                label={t("%EmailAddress.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%EmailAddressLogin.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.checked)}
                title=""
                type="checkbox"
                value={field.state.value.toString()}
              />
            );
          }}
          name="emailAddressLogin"
        />
        <Field name="password">
          {function (field) {
            return (
              <FormField
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                invalid={field.state.meta.errors.length > 0}
                label={t("%Password.0%")}
                required
              >
                <PasswordInput
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={(event) => field.handleChange(event.target.value)}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </Field>
        <Field name="postalCode">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                containerClassName={css({ maxWidth: "120px" })}
                id={field.name}
                label={t("%PostalCode.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field name="address">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                containerClassName={css({ maxWidth: "480px" })}
                id={field.name}
                label={t("%Address.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field
          children={function (field) {
            return (
              <MultilineInput
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "480px" })}
                id={field.name}
                label={t("%Remark.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                title=""
                value={field.state.value}
              />
            );
          }}
          name="remark"
        />

        <div className={css({ display: "flex", gap: "8px" })}>
          <Subscribe
            selector={function ({ isSubmitting }) {
              return isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <Link
                  className={buttonStyles({ color: "secondary" })}
                  disabled={submitting}
                  to="/management/organizations"
                >
                  {t("%Cancel.0%")}
                </Link>
              );
            }}
          </Subscribe>
          <Subscribe
            selector={function ({ canSubmit, isSubmitting }) {
              return !canSubmit || isSubmitting;
            }}
          >
            {function (disabled) {
              return (
                <Button className={css({ width: "max-content" })} disabled={disabled} type="submit">
                  {t("%Save.0%")}
                </Button>
              );
            }}
          </Subscribe>
        </div>
      </form>
    </main>
  );
}
